/**
 * Form tạo/sửa BĐS trong admin — bọc hook useAdminPropertyForm.
 *
 * Luồng dữ liệu:
 * - propertyId null → tạo mới; có id → chỉnh sửa (hook fetch dữ liệu hiện tại).
 * - useAdminPropertyForm quản lý form state, upload ảnh, danh mục, cây địa điểm.
 * - handleSubmit gọi saveProperty → onSaved callback cho modal/page parent reload.
 */
import { ButtonSpinner } from '../Common/ButtonSpinner';

import { ErrorMessage } from '../Common/ErrorMessage';

import { FormattedNumberInput } from '../Common/FormattedNumberInput';

import { ImageFileUpload } from '../Common/ImageFileUpload';

import { InputWithUnit } from '../Common/InputWithUnit';

import { Loading } from '../Common/Loading';

import { AdminFormSection } from './AdminFormSection';

import { AdminPropertySheetForm } from './AdminPropertySheetForm';

import { AmenityToggleField } from './AmenityToggleField';

import { RoomSpecField } from './RoomSpecField';

import { useAdminPropertyForm } from '../../features/admin/properties/hooks/useAdminPropertyForm';

import { PROPERTY_IMAGE_UPLOAD } from '../../shared/constants/files';

import { UI_MESSAGES } from '../../shared/constants/messages';

import {

  PROPERTY_FEATURE_OPTIONS,

  PROPERTY_ROOM_SPEC_OPTIONS,

  PROPERTY_STATUS_OPTIONS,

} from '../../shared/constants/property';

import { TEXT } from '../../shared/languages';

import { getCategoryName } from '../../shared/utils/categoryLabel';



export const AdminPropertyForm = ({

  propertyId = null,

  userRole,

  onSaved,

  formId = 'admin-property-form',

  sheetLayout = false,

}) => {

  const {

    propertyForm,

    categoryList,

    locationTree,

    isEditingProperty,

    isLoadingProperty,

    isSubmittingProperty,

    errorMessage,

    selectedImages,

    publicationStatusOptions,

    updatePropertyField,

    selectCity,

    selectWard,

    selectPropertyImages,

    saveProperty,

  } = useAdminPropertyForm(propertyId, userRole);



  /** Tỉnh đang chọn và danh sách phường con — derive từ locationTree */

  const selectedCity = locationTree.find((city) => city.name === propertyForm.province);

  const wardList = selectedCity?.children || [];

  const form = TEXT.property.form;

  const common = TEXT.common;

  const transactionType = TEXT.property.transactionType;



  /** Hiện section quản lý khi sửa tin hoặc user có quyền đổi trạng thái xuất bản */

  const showManagementSection = isEditingProperty || publicationStatusOptions.length > 0;



  /** Validate qua HTML5 + gọi API lưu qua hook */

  const handleSubmit = async (event) => {

    event.preventDefault();

    const savedPropertyId = await saveProperty();



    if (savedPropertyId) {

      onSaved?.(savedPropertyId);

    }

  };



  if (isLoadingProperty) {

    return (

      <div className={sheetLayout ? 'admin-form-modal-body admin-form-sheet-body' : undefined}>

        <Loading label={UI_MESSAGES.loadingProperty} />

      </div>

    );

  }



  const formClassName = `property-form property-form--grouped${sheetLayout ? ' property-form--sheet' : ''}`.trim();



  const formContent = (

    <form id={formId} className={formClassName} onSubmit={handleSubmit}>

      <ErrorMessage message={errorMessage} />

      {!publicationStatusOptions.length && !isEditingProperty && (

        <div className="info-box">{form.pendingNotice}</div>

      )}



      <div className="property-form-sections">

        <AdminFormSection title={form.sectionBasicTitle} description={form.sectionBasicDesc}>

          <label>

            {form.categoryLabel}

            <select

              value={propertyForm.category_id || ''}

              onChange={(event) => updatePropertyField('category_id', event.target.value)}

              required

            >

              <option value="">{form.selectCategory}</option>

              {categoryList.map((category) => (

                <option key={category.id} value={category.id}>

                  {getCategoryName(category)}

                </option>

              ))}

            </select>

          </label>

          <label>

            {form.transactionLabel}

            <input value={transactionType.sale} readOnly />

          </label>

          <label className="wide">

            {form.titleLabel}

            <input

              value={propertyForm.title || ''}

              onChange={(event) => updatePropertyField('title', event.target.value)}

              required

            />

          </label>

        </AdminFormSection>



        <AdminFormSection title={form.sectionPricingTitle} description={form.sectionPricingDesc}>

          <label>

            {form.priceLabel}

            <InputWithUnit unit={form.priceUnit}>

              <FormattedNumberInput

                min="1"

                value={propertyForm.price}

                onValueChange={(numberValue) => updatePropertyField('price', numberValue)}

                placeholder={form.pricePlaceholder}

                required

              />

            </InputWithUnit>

          </label>

          <label>

            {form.areaLabel}

            <FormattedNumberInput

              min="1"

              value={propertyForm.area}

              onValueChange={(numberValue) => updatePropertyField('area', numberValue)}

              placeholder={form.areaPlaceholder}

              allowDecimal

            />

          </label>

          <label>

            {form.frontageLabel}

            <span className="field-control">

              <FormattedNumberInput

                min="0"

                value={propertyForm.frontage}

                onValueChange={(numberValue) => updatePropertyField('frontage', numberValue)}

                placeholder={form.frontagePlaceholder}

                allowDecimal

              />

            </span>

          </label>

          <label>

            {form.plotLengthLabel}

            <span className="field-control">

              <FormattedNumberInput

                min="0"

                value={propertyForm.plot_length}

                onValueChange={() => {}}

                placeholder={form.plotLengthPlaceholder}

                allowDecimal

                readOnly

              />

              <small className="field-hint">{form.plotLengthHint}</small>

            </span>

          </label>

        </AdminFormSection>



        <AdminFormSection title={form.sectionLocationTitle} description={form.sectionLocationDesc}>

          <label>

            {form.provinceLabel}

            <select

              value={propertyForm.province || ''}

              onChange={(event) => selectCity(event.target.value)}

              required

            >

              <option value="">{form.selectProvince}</option>

              {locationTree.map((city) => (

                <option key={city.id} value={city.name}>

                  {city.name}

                </option>

              ))}

            </select>

          </label>

          <label>

            {form.wardLabel}

            <select

              value={propertyForm.location_id || ''}

              onChange={(event) => selectWard(event.target.value)}

              disabled={!propertyForm.province}

              required

            >

              <option value="">{form.selectWard}</option>

              {wardList.map((ward) => (

                <option key={ward.id} value={ward.id}>

                  {ward.name}

                </option>

              ))}

            </select>

          </label>

        </AdminFormSection>



        <AdminFormSection title={form.sectionSpecsTitle} description={form.sectionSpecsDesc}>

          <div className="wide room-spec-grid">

            {PROPERTY_ROOM_SPEC_OPTIONS.map(({ fieldName, label }) => (

              <RoomSpecField

                key={fieldName}

                label={label}

                value={propertyForm[fieldName]}

                quantityLabel={form.roomSpecQuantityLabel}

                onChange={(nextValue) => updatePropertyField(fieldName, nextValue)}

              />

            ))}

          </div>

        </AdminFormSection>



        <AdminFormSection title={form.sectionAmenitiesTitle} description={form.sectionAmenitiesDesc}>

          <div className="wide room-spec-grid">

            {PROPERTY_FEATURE_OPTIONS.map(({ fieldName, label }) => (

              <AmenityToggleField

                key={fieldName}

                label={label}

                checked={Boolean(propertyForm[fieldName])}

                onChange={(nextValue) => updatePropertyField(fieldName, nextValue)}

              />

            ))}

          </div>

        </AdminFormSection>



        <AdminFormSection title={form.sectionContentTitle} description={form.sectionContentDesc}>

          <label className="wide">

            {common.description}

            <textarea

              rows="6"

              value={propertyForm.description || ''}

              onChange={(event) => updatePropertyField('description', event.target.value)}

              required

            />

          </label>

          <div className="wide field">

            {form.imagesLabel}

            <ImageFileUpload

              accept={PROPERTY_IMAGE_UPLOAD.accept}

              hint={PROPERTY_IMAGE_UPLOAD.hint}

              files={selectedImages}

              onFilesChange={selectPropertyImages}

              disabled={isSubmittingProperty}

            />

          </div>

        </AdminFormSection>



        {showManagementSection && (

          <AdminFormSection title={form.sectionManagementTitle} description={form.sectionManagementDesc}>

            {isEditingProperty && (

              <label>

                {form.statusLabel}

                <select

                  value={propertyForm.status}

                  onChange={(event) => updatePropertyField('status', event.target.value)}

                >

                  {PROPERTY_STATUS_OPTIONS.map((statusOption) => (

                    <option key={statusOption.value} value={statusOption.value}>

                      {statusOption.label}

                    </option>

                  ))}

                </select>

              </label>

            )}

            {publicationStatusOptions.length > 0 && (

              <label>

                {form.publicationLabel}

                <select

                  value={propertyForm.publication_status}

                  onChange={(event) => updatePropertyField('publication_status', event.target.value)}

                >

                  {publicationStatusOptions.map((publicationOption) => (

                    <option key={publicationOption.value} value={publicationOption.value}>

                      {publicationOption.label}

                    </option>

                  ))}

                </select>

              </label>

            )}

          </AdminFormSection>

        )}

      </div>



      {!sheetLayout && (

        <div className="property-form-actions">

          <button className="button" disabled={isSubmittingProperty}>

            {isSubmittingProperty && <ButtonSpinner />}

            {isSubmittingProperty ? common.saving : common.save}

          </button>

        </div>

      )}

    </form>

  );



  if (!sheetLayout) {

    return formContent;

  }



  return (

    <AdminPropertySheetForm

      formId={formId}

      form={form}

      common={common}

      transactionType={transactionType}

      propertyForm={propertyForm}

      categoryList={categoryList}

      locationTree={locationTree}

      wardList={wardList}

      isEditingProperty={isEditingProperty}

      showManagementSection={showManagementSection}

      publicationStatusOptions={publicationStatusOptions}

      selectedImages={selectedImages}

      isSubmittingProperty={isSubmittingProperty}

      errorMessage={errorMessage}

      pendingNoticeVisible={!publicationStatusOptions.length && !isEditingProperty}

      roomSpecOptions={PROPERTY_ROOM_SPEC_OPTIONS}

      featureOptions={PROPERTY_FEATURE_OPTIONS}

      imageUploadConfig={PROPERTY_IMAGE_UPLOAD}

      updatePropertyField={updatePropertyField}

      selectCity={selectCity}

      selectWard={selectWard}

      selectPropertyImages={selectPropertyImages}

      onSubmit={handleSubmit}

    />

  );

};

